import { isApiError } from "@sagot/api-client";
import { planAtLeast, WORKFLOW_STEP_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Icon, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import { useCancelRun, useConversationRuns, useStartConversationRun } from "../workflows/use-workflows-p4";
import { useWorkflowList } from "../workflows/use-workflows";

const M = "engage.workflows.inbox";
const RUN_PLAN = WORKFLOW_STEP_MIN_PLAN.run_workflow ?? "pro";
/** Why the server refused to start a run (`error.reason` of the 409). */
const START_REFUSALS = ["not_live", "run_active", "person_has_conversation", "switched_off"] as const;
const RECENT_RUNS = 3;

interface RunWorkflowMenuProps {
  conversationId: string;
  /** The member may reply in this conversation (`inbox.reply`). */
  canReply: boolean;
}

/**
 * "Run workflow": staff start a live reusable workflow on the conversation (Pro and up; needs `inbox.reply` and
 * `workflows.view`), and stop one of its active runs. Renders nothing when the member or the plan cannot use it.
 */
export function RunWorkflowMenu({ conversationId, canReply }: RunWorkflowMenuProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const permissions = usePermissions();
  const plan = useWorkspaceDetails().data?.plan;
  const allowed = canReply && permissions.can("workflows.view", { anywhere: true }) && plan !== undefined && planAtLeast(plan, RUN_PLAN);
  const workflows = useWorkflowList(allowed);
  const runs = useConversationRuns(conversationId, allowed);
  const start = useStartConversationRun(conversationId);
  const cancel = useCancelRun();
  if (!allowed) return null;
  const reusable = (workflows.data ?? []).filter((workflow) => workflow.trigger === "reusable" && workflow.state === "live" && workflow.kind === "background");
  const running = (runs.data ?? []).filter((run) => run.cancellable).slice(0, RECENT_RUNS);
  const run = (workflowId: string, name: string) => {
    start.mutate(workflowId, {
      onSuccess: () => { toast.success(t(`${M}.started`, { name })); },
      onError: (error) => {
        const reason = isApiError(error) && error.status === 409 ? START_REFUSALS.find((candidate) => candidate === error.details.reason) : undefined;
        toast.error(reason ? t(`${M}.refused.${reason}`) : message(error));
      },
    });
  };
  const stop = (runId: string, name: string) => {
    cancel.mutate(runId, {
      onSuccess: () => { toast.success(t(`${M}.stopped`, { name })); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" loading={start.isPending} data-testid="run-workflow-menu">
          <Icon name="workflow" />
          {t(`${M}.button`)}
          <Icon name="chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>{t(`${M}.label`)}</DropdownMenuLabel>
        {reusable.length === 0 ? <p className="px-2.5 pb-2 text-xs text-fg-subtle" data-testid="run-workflow-empty">{workflows.isPending ? t(`${M}.loading`) : t(`${M}.empty`)}</p> : null}
        {reusable.map((workflow) => (
          <DropdownMenuItem key={workflow.id} onSelect={() => { run(workflow.id, workflow.name); }}>
            <Icon name="play" className="size-4 text-fg-subtle" />
            <span className="truncate">{workflow.name}</span>
          </DropdownMenuItem>
        ))}
        {running.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t(`${M}.active`)}</DropdownMenuLabel>
            {running.map((item) => (
              <DropdownMenuItem key={item.id} onSelect={() => { stop(item.id, item.workflow_name); }}>
                <Icon name="square" className="size-4 text-fg-subtle" />
                <span className="truncate">{t(`${M}.stop`, { name: item.workflow_name })}</span>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
